import { useCallback, useEffect, useState } from 'react';
import { fetchAuditLogs } from '../../api/companyPlan';
import { AuditLogItem } from '../../types/company';
import { reportError } from '../../utils/notify';
import ListPager from '../common/ListPager';
import '../quality/QualityShell.css';
import './AuditLogPanel.css';

const PAGE_SIZE = 20;

function errMessage(err: unknown, fallback: string) {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback
  );
}

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  // 서버 LocalDateTime(타임존 없음)은 한국 벽시계로 저장된 값 → 변환 없이 표시
  return value.replace('T', ' ').slice(0, 19);
}

function AuditLogPanel() {
  const [rows, setRows] = useState<AuditLogItem[]>([]);
  const [action, setAction] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (pageNum = 0) => {
    try {
      setLoading(true);
      setError('');
      const result = await fetchAuditLogs({
        action: action || undefined,
        page: pageNum,
        size: PAGE_SIZE,
      });
      setRows(result.content || []);
      setPage(result.number ?? pageNum);
      setTotalPages(result.totalPages ?? 0);
      setTotalElements(result.totalElements ?? result.content?.length ?? 0);
    } catch (err) {
      reportError(setError, errMessage(err, '감사 로그를 불러오지 못했습니다.'));
      setRows([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [action]);

  useEffect(() => {
    setPage(0);
    void load(0);
  }, [load]);

  return (
    <main className="quality-shell audit-log-panel">
      <div className="quality-shell__toolbar">
        <div>
          <h2>감사 로그</h2>
          <p>
            프로 요금제 오너 전용 · 결재(상신·승인·반려·취소·완료), 문서 바로승인·승인철회,
            등록·수정·삭제, 이름 변경, 게스트·PDF·요금제 변경을 기록합니다.
          </p>
        </div>
        <div className="audit-log-panel__tools">
          <select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            aria-label="액션 필터"
          >
            <option value="">전체 액션</option>
            <option value="APPROVAL_CREATE">결재 상신</option>
            <option value="APPROVAL_STEP_APPROVE">결재 승인</option>
            <option value="APPROVAL_REJECT">결재 반려</option>
            <option value="APPROVAL_CANCEL">결재 취소</option>
            <option value="APPROVAL_COMPLETE">결재 완료</option>
            <option value="DOC_APPROVE">문서 바로 승인</option>
            <option value="DOC_REVOKE">승인 철회</option>
            <option value="DOC_CREATE">문서 등록</option>
            <option value="DOC_UPDATE">문서 수정</option>
            <option value="DOC_DELETE">문서 삭제</option>
            <option value="GUEST_ISSUE">게스트 발급</option>
            <option value="GUEST_REVOKE">게스트 철회</option>
            <option value="PDF_DOWNLOAD">PDF 출력</option>
            <option value="PLAN_CHANGE">요금제 변경</option>
            <option value="PROFILE_NAME_CHANGE">이름 변경</option>
          </select>
          <button type="button" className="quality-tabs__btn" onClick={() => void load(page)}>
            새로고침
          </button>
        </div>
      </div>

      {error ? <p className="quality-error">{error}</p> : null}

      <div className="paged-list">
        {loading ? (
          <p className="paged-list__status">불러오는 중…</p>
        ) : (
          <>
            <div className="paged-list__scroll">
              <table className="quality-table">
                <thead>
                  <tr>
                    <th style={{ width: 170 }}>시각</th>
                    <th>내용</th>
                    <th style={{ width: 120 }}>수행자</th>
                    <th style={{ width: 100 }}>액션</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={4}>기록이 없습니다.</td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.auditId}>
                        <td>{formatDateTime(row.createdAt)}</td>
                        <td>
                          {row.summary ||
                            [row.targetTypeLabel || row.targetType, row.detail, row.actionLabel]
                              .filter(Boolean)
                              .join(' · ') ||
                            '-'}
                        </td>
                        <td>{row.actorName || '-'}</td>
                        <td>{row.actionLabel || row.action}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <ListPager
              page={page}
              totalPages={totalPages}
              totalElements={totalElements}
              loading={loading}
              onChange={(next) => void load(next)}
            />
          </>
        )}
      </div>
    </main>
  );
}

export default AuditLogPanel;
